/**
 * cinematicAutoPreset.js — AI Auto-Preset по DICOM tags
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6.1
 *
 * [ULTRA UPGRADED: Smart LRU-Cache, Immutability, ES2022 Syntax, Deadlock Protection]
 */

import { log } from '../utils/logger.js';

/**
 * @typedef {Object} DicomMeta
 * @property {boolean} _isExtracted — Флаг, что данные уже обработаны
 * @property {string} modality — CT, MR, PT, XA, US, CR, DX, ...
 * @property {string} seriesDesc — Series Description (0008,103E)
 * @property {string} studyDesc — Study Description (0008,1030)
 * @property {string} bodyPart — Body Part Examined (0018,0015)
 * @property {number} sliceThickness — мм
 * @property {number} kvp — пиковое напряжение трубки (0018,0060)
 * @property {string} convolutionKernel — Convolution Kernel (0018,1210)
 * @property {string} protocolName — Protocol Name (0018,1030)
 * @property {number} rows — Rows
 * @property {number} columns — Columns
 * @property {string} _uid — Series Instance UID
 */

// ═══════════════════════════════════════════════════════════════
// Compiled Dictionaries (Сверхбыстрый поиск + Immutability)
// Object.freeze защищает от мутаций и ускоряет доступ в JS Engine
// ═══════════════════════════════════════════════════════════════

const DICT = Object.freeze({
  lung: /lung|pulmo|лёгк|легк|thorax|chest|грудн/i,
  chestPart: /chest|lung|thorax/i,
  ctLungKernel: /lung|b70|b80|sharp/i,

  boneDesc: /bone|кост|skeletal|spine|позвон|hip|тазобедр|fracture|перелом/i,
  bonePart: /spine|pelvis|hip|extremity|knee|ankle|shoulder/i,
  ctBoneKernel: /bone|b60|b70|u70/i,

  brainDesc: /brain|head|мозг|голов|cranial|neuro|cerebr/i,
  brainPart: /head|brain|skull/i,

  abdoDesc: /abdom|liver|печ|kidney|почк|abdo|живот|брюш/i,
  abdoPart: /abdomen|liver|kidney|pelvis/i,

  angioDesc: /angio|cta|ангио|arter|vascul|сосуд|аорт|aorta/i,
  angioProto: /angio|cta|bolus/i,

  mrT1: /t1|mprage|bravo|flair/i,
  mrT2: /t2|stir|fse/i,
  mrDiff: /dti|diffusion|dwi|adc/i,
  mrAngio: /mra|angio|tof|ce-mra/i
});

// ═══════════════════════════════════════════════════════════════
// Rules Graph (АДДИТИВНАЯ математика баллов)
// ═══════════════════════════════════════════════════════════════

const RULES_DB = Object.freeze({
  CT: [
    { match: m => DICT.lung.test(m.seriesDesc), preset: 'xray', score: 90, reason: 'CT Лёгкие → X-Ray Glow' },
    { match: m => DICT.chestPart.test(m.bodyPart), preset: 'xray', score: 70, reason: 'CT Chest/Lung → X-Ray Glow' },
    { match: m => DICT.ctLungKernel.test(m.convolutionKernel), preset: 'xray', score: 75, reason: 'CT Lung kernel → X-Ray Glow' },
    
    { match: m => DICT.boneDesc.test(m.seriesDesc), preset: 'highContrast', score: 90, reason: 'CT Кость → High Contrast' },
    { match: m => DICT.bonePart.test(m.bodyPart), preset: 'highContrast', score: 65, reason: 'CT MSK region → High Contrast' },
    { match: m => DICT.ctBoneKernel.test(m.convolutionKernel), preset: 'highContrast', score: 70, reason: 'CT Bone kernel → High Contrast' },
    
    { match: m => DICT.brainDesc.test(m.seriesDesc), preset: 'surgical', score: 85, reason: 'CT Head/Brain → Surgical' },
    { match: m => DICT.brainPart.test(m.bodyPart), preset: 'surgical', score: 70, reason: 'CT Head region → Surgical' },
    
    { match: m => DICT.abdoDesc.test(m.seriesDesc), preset: 'anatomical', score: 85, reason: 'CT Abdomen → Anatomical' },
    { match: m => DICT.abdoPart.test(m.bodyPart), preset: 'anatomical', score: 65, reason: 'CT Abdomen region → Anatomical' },
    
    { match: m => DICT.angioDesc.test(m.seriesDesc), preset: 'neon', score: 90, reason: 'CT Angio → Neon' },
    { match: m => DICT.angioProto.test(m.protocolName), preset: 'neon', score: 75, reason: 'CT Angio protocol → Neon' },
    
    { match: () => true, preset: 'cinematic', score: 30, reason: 'CT (general) → Cinematic' }
  ],
  MR: [
    { match: m => DICT.mrT1.test(m.seriesDesc), preset: 'anatomical', score: 85, reason: 'MRI T1/FLAIR → Anatomical' },
    { match: m => DICT.mrT2.test(m.seriesDesc), preset: 'cinematic', score: 80, reason: 'MRI T2 → Cinematic' },
    { match: m => DICT.mrDiff.test(m.seriesDesc), preset: 'xray', score: 80, reason: 'MRI Diffusion → X-Ray Glow' },
    { match: m => DICT.mrAngio.test(m.seriesDesc), preset: 'neon', score: 85, reason: 'MR Angiography → Neon' },
    { match: () => true, preset: 'cinematic', score: 25, reason: 'MRI (general) → Cinematic' }
  ],
  PT: [ { match: () => true, preset: 'neon', score: 80, reason: 'PET/NM → Neon (эмиссия)' } ],
  NM: [ { match: () => true, preset: 'neon', score: 80, reason: 'PET/NM → Neon (эмиссия)' } ],
  CR: [ { match: () => true, preset: 'surgical', score: 60, reason: 'X-Ray → Surgical' } ],
  DX: [ { match: () => true, preset: 'surgical', score: 60, reason: 'X-Ray → Surgical' } ],
  US: [ { match: () => true, preset: 'surgical', score: 50, reason: 'US → Surgical' } ],
  
  GLOBAL: [
    { match: m => m.sliceThickness > 0 && m.sliceThickness <= 1.0 && m.modality === 'CT', preset: 'cinematic', score: 15, reason: 'Thin slices (≤1mm) → boost Cinematic' },
    { match: m => m.sliceThickness >= 5.0 && m.modality === 'CT', preset: 'surgical', score: 10, reason: 'Thick slices (≥5mm) → Surgical' },
    { match: m => m.rows >= 512 && m.columns >= 512, preset: 'cinematic', score: 10, reason: 'High resolution → Cinematic' }
  ]
});

// ═══════════════════════════════════════════════════════════════
// Core System
// ═══════════════════════════════════════════════════════════════

const MAX_CONFIDENCE_DIVIDER = 200; // Оригинальная константа совпадения 100%
const MAX_CACHE_SIZE = 500;
const CACHE = new Map();

// Ускоренная и безопасная функция нормализации строк
const _safeStr = val => (Array.isArray(val) ? val.join(' ') : String(val ?? '')).trim();

/**
 * Извлекает DICOM метаданные безопасно
 * @param {object} series
 * @returns {DicomMeta}
 */
export function extractMetaFromSeries(series = {}) {
  return {
    _isExtracted: true, // Флаг для избежания повторного парсинга
    modality: _safeStr(series.modality).toUpperCase(),
    seriesDesc: _safeStr(series.seriesDesc ?? series.seriesDescription),
    studyDesc: _safeStr(series.studyDesc ?? series.studyDescription),
    bodyPart: _safeStr(series.bodyPart ?? series.bodyPartExamined),
    sliceThickness: Number(series.sliceThickness) || 0,
    kvp: Number(series.kvp) || 0,
    convolutionKernel: _safeStr(series.convolutionKernel),
    protocolName: _safeStr(series.protocolName),
    rows: Number(series.rows) || 0,
    columns: Number(series.columns) || 0,
    _uid: _safeStr(series.seriesInstanceUID)
  };
}

/**
 * API для UI (возвращает плоский массив правил через ES2019 .flat())
 */
export const getAllRules = () => Object.values(RULES_DB)
  .flat()
  .map(({ preset, score, reason }) => ({ preset, score, reason }));

/**
 * Подсчитывает баллы и возвращает рекомендацию
 */
export function getPresetRecommendation(rawMeta) {
  if (!rawMeta?.modality) {
    return { presetId: 'cinematic', confidence: 0, reason: 'No metadata', scores: {} };
  }

  // Если уже распарсено — используем, иначе парсим
  const meta = rawMeta._isExtracted ? rawMeta : extractMetaFromSeries(rawMeta);
  
  // Уникальный ключ. Если нет UID — создаем хеш по свойствам
  const cacheKey = meta._uid || `${meta.modality}_${meta.seriesDesc}_${meta.bodyPart}_${meta.sliceThickness}`;
  
  // LRU Cache: если есть в кэше, переносим в конец (как самый свежий)
  if (CACHE.has(cacheKey)) {
    const cached = CACHE.get(cacheKey);
    CACHE.delete(cacheKey);
    CACHE.set(cacheKey, cached);
    return cached;
  }

  const scores = {};
  const reasons = {};
  const activeRules = [...(RULES_DB[meta.modality] || []), ...RULES_DB.GLOBAL];

  // Сборка баллов
  for (const rule of activeRules) {
    try {
      if (rule.match(meta)) {
        const pid = rule.preset;
        scores[pid] = (scores[pid] || 0) + rule.score;
        (reasons[pid] ??= []).push(rule.reason);
      }
    } catch (e) {
      log.warn(`[AutoPreset] Rule eval failed for ${rule.preset}:`, e);
    }
  }

  // Функциональный поиск пресета с максимальным скором (без let)
  const [bestPreset, bestScore] = Object.entries(scores)
    .reduce((best, current) => current[1] > best[1] ? current : best, ['cinematic', 0]);

  const confidence = Math.min(100, Math.round((bestScore / MAX_CONFIDENCE_DIVIDER) * 100));

  const result = {
    presetId: bestPreset,
    confidence,
    reason: (reasons[bestPreset] || []).join('; '),
    scores,
  };

  // LRU Eviction: удаляем самый старый (первый элемент в Map)
  if (CACHE.size >= MAX_CACHE_SIZE) {
    CACHE.delete(CACHE.keys().next().value);
  }
  CACHE.set(cacheKey, result);

  return result;
}

// ═══════════════════════════════════════════════════════════════
// Application Action
// ═══════════════════════════════════════════════════════════════

let _isApplying = false;

// Утилита для защиты от вечного лока при падении динамического импорта
const withTimeout = (promise, ms) => Promise.race([
  promise,
  new Promise((_, reject) => setTimeout(() => reject(new Error('Morphing Timeout')), ms))
]);

export async function autoSelectPreset(renderer, metaOrSeries, opts = {}) {
  try {
    if (!renderer || _isApplying) return { applied: false, reason: 'Renderer busy or missing' };
  
    const { animate = true, duration = 800, minConfidence = 20 } = opts;
    const recommendation = getPresetRecommendation(metaOrSeries);
  
    log.debug(`[AutoPreset] ${metaOrSeries?.modality || 'UNK'} → ${recommendation.presetId} (${recommendation.confidence}%) — ${recommendation.reason}`);
  
    if (recommendation.confidence < minConfidence) {
      log.debug(`[AutoPreset] Confidence < ${minConfidence}%, aborted.`);
      return { ...recommendation, applied: false };
    }
  
    _isApplying = true;
  
    try {
      if (animate) {
        // Динамический импорт с защитой от долгого ответа сети
        const { morphToPreset } = await import('./cinematicMorph.js');
        await withTimeout(morphToPreset(renderer, recommendation.presetId, { duration }), duration + 2000);
      } else {
        renderer.setPreset?.(recommendation.presetId);
      }
    } catch (e) {
      log.warn(`[AutoPreset] Animation failed, fallback to hard set.`, e);
      // Fallback если импорт отвалился или анимация крашнулась
      renderer.setPreset?.(recommendation.presetId);
    } finally {
      _isApplying = false;
    }
  
    return { ...recommendation, applied: true };
  } catch (e) { log.warn(`[cinematicAutoPreset] autoSelectPreset failed:`, e.message); return {}; }
}

// ── [v8] Enterprise API ──
export function getAutoPresetState() {
  return { cacheSize: CACHE.size, maxCache: MAX_CACHE_SIZE, isApplying: _isApplying, ruleCount: Object.values(RULES_DB).flat().length };
}
export function clearAutoPresetCache() { CACHE.clear(); return true; }
export function getSupportedModalities() { return Object.keys(RULES_DB).filter(k => k !== 'GLOBAL'); }